import type { MeetingEventLiveStreamResponse, MeetingStatus } from '@govinity/validation';
import { MeetingStatus as MeetingStatusEnum } from '@govinity/validation';

/**
 * Whether the meeting page should play live video: the meeting is live, its stream is active, and it
 * is the meeting the continuous broadcast is currently airing. A live meeting that is off air (the
 * broadcast is airing a sibling on the same encoder) resolves to false, so no player mounts.
 *
 * Split out of the layout so the on-air gating rule — including the `isOnAir` discriminator — is
 * unit-tested rather than buried in JSX.
 *
 * @param params - Live state inputs.
 * @param params.isMeetingLive - Whether the meeting's status is `Live`.
 * @param params.liveStreamData - The live-stream response, or null/undefined before it loads.
 * @returns Whether to render the live player.
 */
export function hasLiveVideo({
  isMeetingLive,
  liveStreamData,
}: {
  isMeetingLive: boolean;
  liveStreamData:
    | Pick<MeetingEventLiveStreamResponse, 'muxPlaybackId' | 'isStreamActive' | 'isOnAir'>
    | null
    | undefined;
}): boolean {
  return (
    isMeetingLive &&
    !!liveStreamData?.muxPlaybackId &&
    !!liveStreamData?.isStreamActive &&
    !!liveStreamData?.isOnAir
  );
}

/** Whether and how to render a live badge for a meeting in the portal. */
export interface LiveBadgeState {
  /** Show a live badge at all (the meeting is `Live`). */
  show: boolean;
  /** Render the darker "Live - Off Air" treatment instead of the plain "Live" badge. */
  offAir: boolean;
  /** Render the orange "In Recess" treatment. Takes precedence over `offAir`. */
  recess: boolean;
}

/**
 * Decides the live-badge treatment for a meeting shown in a portal list or header.
 *
 * Off-air is only asserted once `isOnAir` is known to be false; while it is undefined (still loading,
 * or a surface with no broadcast awareness) the badge defaults to the plain on-air treatment so it
 * never flashes "Off Air" before the data resolves. Recess wins over off-air: `isRecessed` is a
 * backend-computed flag that is true for both a streaming parked recess (off air) and an encoderless
 * recess (on air), so it must suppress the off-air treatment rather than sit beside it.
 *
 * @param params - Badge inputs.
 * @param params.status - The meeting status.
 * @param params.isOnAir - Whether the meeting is on air, or undefined when unknown.
 * @param params.isRecessed - Whether the meeting is in recess, or undefined when unknown.
 * @returns The resolved badge state.
 */
export function resolveLiveBadge({
  status,
  isOnAir,
  isRecessed,
}: {
  status: MeetingStatus;
  isOnAir: boolean | undefined;
  isRecessed?: boolean | undefined;
}): LiveBadgeState {
  const isLive = status === MeetingStatusEnum.Live;
  const recess = isLive && isRecessed === true;
  return { show: isLive, offAir: isLive && isOnAir === false && !recess, recess };
}
